import dayjs from 'dayjs';
import { TIME_PERIOD_PRESETS, MAX_TIME_PERIOD_DAYS } from '../constants/documentReportFilterConstants';

const todayEnd = () => dayjs().endOf('day');

const capEndDateAtToday = (endDate) => {
  const today = todayEnd();
  if (!endDate || endDate.isAfter(today)) {
    return today;
  }
  return endDate;
};

export const computeTimePeriodDates = (preset) => {
  const today = dayjs().startOf('day');
  const todayEndOfDay = todayEnd();

  switch (preset) {
    case TIME_PERIOD_PRESETS.TODAY:
      return { startDate: today, endDate: todayEndOfDay };
    case TIME_PERIOD_PRESETS.YESTERDAY: {
      const yesterday = today.subtract(1, 'day');
      return { startDate: yesterday, endDate: yesterday.endOf('day') };
    }
    case TIME_PERIOD_PRESETS.THIS_WEEK:
      return {
        startDate: today.startOf('week'),
        endDate: capEndDateAtToday(today.endOf('week')),
      };
    case TIME_PERIOD_PRESETS.LAST_WEEK: {
      const lastWeek = today.subtract(1, 'week');
      return { startDate: lastWeek.startOf('week'), endDate: lastWeek.endOf('week') };
    }
    case TIME_PERIOD_PRESETS.THIS_MONTH:
      return {
        startDate: today.startOf('month'),
        endDate: capEndDateAtToday(today.endOf('month')),
      };
    case TIME_PERIOD_PRESETS.LAST_MONTH: {
      const lastMonth = today.subtract(1, 'month');
      return { startDate: lastMonth.startOf('month'), endDate: lastMonth.endOf('month') };
    }
    case TIME_PERIOD_PRESETS.LAST_7_DAYS:
      return { startDate: today.subtract(6, 'day'), endDate: todayEndOfDay };
    case TIME_PERIOD_PRESETS.LAST_30_DAYS:
      return { startDate: today.subtract(29, 'day'), endDate: todayEndOfDay };
    default:
      return { startDate: null, endDate: null };
  }
};

export const getTimePeriodDayCount = (startDate, endDate) => {
  if (!startDate || !endDate) {
    return 0;
  }

  return endDate.startOf('day').diff(startDate.startOf('day'), 'day') + 1;
};

export const isTimePeriodValid = (startDate, endDate) => {
  if (!startDate || !endDate) {
    return false;
  }

  const today = dayjs().startOf('day');
  if (startDate.startOf('day').isAfter(today, 'day') || endDate.startOf('day').isAfter(today, 'day')) {
    return false;
  }

  const dayCount = getTimePeriodDayCount(startDate, endDate);
  return (
    dayCount > 0 && dayCount <= MAX_TIME_PERIOD_DAYS && !startDate.startOf('day').isAfter(endDate.startOf('day'), 'day')
  );
};

export const getTimePeriodPresetLabel = (preset, labels = {}) => {
  const presetLabels = {
    [TIME_PERIOD_PRESETS.TODAY]: labels.today ?? 'Today',
    [TIME_PERIOD_PRESETS.YESTERDAY]: labels.yesterday ?? 'Yesterday',
    [TIME_PERIOD_PRESETS.THIS_WEEK]: labels.this_week ?? 'This Week',
    [TIME_PERIOD_PRESETS.LAST_WEEK]: labels.last_week ?? 'Last Week',
    [TIME_PERIOD_PRESETS.THIS_MONTH]: labels.this_month ?? 'This Month',
    [TIME_PERIOD_PRESETS.LAST_MONTH]: labels.last_month ?? 'Last Month',
    [TIME_PERIOD_PRESETS.LAST_7_DAYS]: labels.last_7_days ?? 'Last 7 days',
    [TIME_PERIOD_PRESETS.LAST_30_DAYS]: labels.last_30_days ?? 'Last 30 days',
    [TIME_PERIOD_PRESETS.CUSTOM]: labels.custom ?? 'Custom Range',
  };

  return presetLabels[preset] ?? preset;
};

export const formatTimePeriodDisplayLabel = ({ preset, startDate, endDate, labels = {}, placeholder = '' }) => {
  if (preset && preset !== TIME_PERIOD_PRESETS.CUSTOM) {
    return getTimePeriodPresetLabel(preset, labels);
  }

  if (startDate && endDate) {
    return `${startDate.format('DD MMM YYYY')} – ${endDate.format('DD MMM YYYY')}`;
  }

  if (startDate) {
    return startDate.format('DD MMM YYYY');
  }

  return placeholder;
};
